// The access routes of the Engine API: who the signed in person is (GET /api/me), the owner's member
// list and writes (/api/access/members) and the audit log (GET /api/audit-log). Bearer token only,
// no credentials in the address. A read that answers 404 or 503 is "not connected", any other failure
// is an error, and a body that does not have the expected shape is an error, never an empty list.
export class AccessClientError extends Error {
  constructor(message, code, status = 0) {
    super(message)
    this.code = code
    this.status = status
  }
}

const fail = (message, code = 'INVALID_RESPONSE', status = 0) => { throw new AccessClientError(message, code, status) }
const isObject = x => x !== null && typeof x === 'object' && !Array.isArray(x)
const text = (x, max = 254) => typeof x === 'string' && x.length > 0 && x.length <= max
const time = x => Number.isSafeInteger(x) && x >= 0
const ID = /^[A-Za-z0-9_-]{1,120}$/

export const SERVER_ROLES = ['owner', 'operator', 'restricted_operator']
export const GRANTABLE_ROLES = ['operator', 'restricted_operator']
export const GATES = ['pull', 'draft', 'release', 'spend']
export const MAX_RESPONSE_BYTES = 1048576

export const isNotConnected = e => e?.code === 'NOT_CONNECTED'

function validMe(v) {
  return isObject(v) && text(v.personId, 120) && (v.email === null || v.email === undefined || text(v.email)) &&
    Array.isArray(v.workspaces) && v.workspaces.length <= 50 && v.workspaces.every(w =>
      isObject(w) && ID.test(w.workspaceId ?? '') && SERVER_ROLES.includes(w.role) &&
      Array.isArray(w.permissions) && w.permissions.length <= 60 && w.permissions.every(p => text(p, 80)) &&
      isObject(w.approvals) && GATES.every(g => typeof w.approvals[g] === 'boolean'))
}

function validMembers(v) {
  const span = m => isObject(m) && ID.test(m.workspaceId ?? '') && time(m.beganMs) && (m.endedMs === null || time(m.endedMs))
  return isObject(v) && Array.isArray(v.members) && v.members.length <= 500 && v.members.every(p =>
    isObject(p) && ID.test(p.personId ?? '') && (p.email === null || text(p.email)) &&
    Array.isArray(p.memberships) && p.memberships.every(m => span(m) && SERVER_ROLES.includes(m.role)) &&
    Array.isArray(p.approvals) && p.approvals.every(a => span(a) && GATES.includes(a.gate)))
}

function validAudit(v) {
  return isObject(v) && ID.test(v.workspaceId ?? '') && Array.isArray(v.rows) && v.rows.length <= 50 &&
    (v.nextCursor === null || text(v.nextCursor, 600)) && v.rows.every(r =>
      isObject(r) && text(r.source, 80) && text(r.action, 80) && time(r.atMs) &&
      (r.person === null || text(r.person, 160)) && isObject(r.detail))
}

const READ_FAILURE = {
  401: ['Sign in again to continue.', 'SESSION_EXPIRED'],
  403: ['This account is not allowed to see that.', 'NOT_ALLOWED'],
}
const WRITE_FAILURE = {
  400: ['Engine did not accept that. Check the details and try again.', 'INVALID_REQUEST'],
  401: ['Sign in again to continue.', 'SESSION_EXPIRED'],
  403: ['Engine refused that change.', 'NOT_ALLOWED'],
  404: ['That access is no longer active. Reload the list.', 'NOT_FOUND'],
  409: ['That conflicts with the current access. Reload the list and try again.', 'CONFLICT'],
}

export function createAccessClient({ baseUrl, getToken, fetchImpl = fetch, allowLocal = false, timeoutMs = 15000 } = {}) {
  let url
  try { url = new URL(baseUrl) } catch { fail('The Engine API is not configured.', 'INVALID_CONFIG') }
  const local = allowLocal && url.protocol === 'http:' && ['localhost', '127.0.0.1', '[::1]'].includes(url.hostname)
  if ((url.protocol !== 'https:' && !local) || url.username || url.password || url.pathname !== '/' || url.search || url.hash || typeof getToken !== 'function') {
    fail('The Engine API configuration is invalid.', 'INVALID_CONFIG')
  }

  async function request(method, path, { query, body, signal, write = false } = {}) {
    // The audit cursor is already a percent encoded value from the server; it goes out as given.
    const qs = Object.entries(query || {}).filter(([, v]) => v !== null && v !== undefined)
      .map(([k, v]) => `${k}=${k === 'cursor' ? v : encodeURIComponent(v)}`).join('&')
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), timeoutMs)
    const onAbort = () => controller.abort()
    signal?.addEventListener('abort', onAbort, { once: true })
    try {
      let token
      try { token = await getToken() } catch { fail('Sign in again to continue.', 'SESSION_EXPIRED', 401) }
      if (typeof token !== 'string' || !token) fail('Sign in again to continue.', 'SESSION_EXPIRED', 401)
      let response
      try {
        response = await fetchImpl(url.origin + path + (qs ? `?${qs}` : ''), {
          method, signal: controller.signal, credentials: 'omit', cache: 'no-store', redirect: 'error',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          ...(body === undefined ? {} : { body: JSON.stringify(body) }),
        })
      } catch (e) {
        if (signal?.aborted) throw new DOMException('Request cancelled.', 'AbortError')
        fail(controller.signal.aborted ? 'Engine took too long to answer.' : 'Engine could not be reached.', 'NETWORK')
      }
      if (!response.ok) {
        if (!write && (response.status === 404 || response.status === 503)) fail('Not connected yet.', 'NOT_CONNECTED', response.status)
        const [message, code] = (write ? WRITE_FAILURE : READ_FAILURE)[response.status] || ['Engine could not complete that.', 'FAILED']
        fail(message, code, response.status)
      }
      const raw = await response.text()
      if (raw.length > MAX_RESPONSE_BYTES) fail('Engine sent an answer that is too large.')
      try { return JSON.parse(raw) } catch { return fail('Engine sent an answer that could not be read.') }
    } finally {
      clearTimeout(timer)
      signal?.removeEventListener('abort', onAbort)
    }
  }

  const valid = (value, check) => check(value) ? value : fail('Engine sent an answer in an unexpected shape.')
  return Object.freeze({
    async me({ signal } = {}) { return valid(await request('GET', '/api/me', { signal }), validMe) },
    async members({ signal } = {}) { return valid(await request('GET', '/api/access/members', { signal }), validMembers) },
    async auditLog({ workspace, cursor, signal } = {}) { return valid(await request('GET', '/api/audit-log', { query: { workspace, cursor }, signal }), validAudit) },
    grant({ personId, email, workspaceId, role }) {
      return request('POST', '/api/access/members', { body: { personId, email, workspaceId, role }, write: true })
    },
    changeRole({ personId, workspaceId, role }) {
      return request('PUT', `/api/access/members/${encodeURIComponent(personId)}`, { body: { workspaceId, role }, write: true })
    },
    endAccess({ personId, workspaceId }) {
      return request('POST', `/api/access/members/${encodeURIComponent(personId)}/end`, { body: { workspaceId }, write: true })
    },
  })
}
